import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { clientsAPI, familyMembersAPI, planTemplatesAPI } from '../services/api';
import { PlusIcon, PencilIcon, TrashIcon, UsersIcon, MagnifyingGlassIcon, UserGroupIcon, ArrowTrendingUpIcon, UserPlusIcon, CheckIcon, ChevronRightIcon, ChevronLeftIcon, FunnelIcon, XMarkIcon, ArrowsUpDownIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { Button, Modal, Card, Input, ConfirmModal } from '../components/ui';
import { DataTableCard } from '../components/ui';
import {
  ClientFiltersToolbar,
  ClientTable,
  ClientImportModal,
  ClientExportMenu,
  deriveClientStatus,
  type ClientSortField,
  type FamilyCountFilter,
  type DerivedClientStatus
} from '../components/clients';
import { ClientFiltersSheet } from '../components/clients/ClientFiltersSheet';
import { Client, ClientFormData } from '../types';
import { validateDominicanID, calculateAge, validateMinimumAge, cleanDominicanID, applyDominicanIDMask, getMaxDateOfBirth, getMinDateOfBirth } from '../utils/validation';
import toast from 'react-hot-toast';
import { ActivateServiceModal } from '../components/ActivateServiceModal';
import { ClientDetailsModal } from '../components/client-profile/ClientDetailsModal';
const PhoneInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  error?: string;
}> = ({ value, onChange, error }) => {
  const [inputValue, setInputValue] = useState('');

  // Convert string "123,456" to array ["123", "456"]
  const phones = value ? value.split(',').map(p => p.trim()).filter(Boolean) : [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addPhone();
    }
  };

  const addPhone = () => {
    if (!inputValue.trim()) return;
    const newPhones = [...phones, inputValue.trim()];
    onChange(newPhones.join(', '));
    setInputValue('');
  };

  const removePhone = (index: number) => {
    const newPhones = phones.filter((_, i) => i !== index);
    onChange(newPhones.join(', '));
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Teléfonos
      </label>
      <div className={`min-h-[42px] p-1.5 border rounded-md shadow-sm bg-white flex flex-wrap gap-2 focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 ${error ? 'border-red-300' : 'border-gray-300'}`}>
        {phones.map((phone, index) => (
          <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-sm font-medium bg-primary-50 text-primary-700 border border-primary-100">
            {phone}
            <button
              type="button"
              onClick={() => removePhone(index)}
              className="ml-1 text-primary-400 hover:text-primary-600 focus:outline-none"
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={addPhone}
          placeholder={phones.length === 0 ? "Ej: 809-555-0000 (Enter para agregar)" : "Agregar otro..."}
          className="flex-1 min-w-[150px] border-none focus:ring-0 p-1 text-gray-900 placeholder-gray-400 sm:text-sm"
        />
      </div>
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      <p className="text-xs text-gray-500 mt-1">Presione Enter para agregar múltiples números.</p>
    </div>
  );
};

// Definitions for Wizard Steps (Moved outside to prevent re-renders)
const steps = [
  { name: 'Datos Personales', fields: ['name', 'lastname', 'cedula', 'dateOfBirth', 'carnetNo', 'categoria'] },
  { name: 'Contacto y Ubicación', fields: ['nacionalidad', 'address', 'telefonos'] },
  { name: 'Afiliación', fields: ['servicePlanId', 'gremio', 'fechaIngreso', 'startDate', 'cuotaMensual', 'pagoInscripcion'] }
];

const ClientModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  client?: Client | null;
  onSave: (data: ClientFormData) => void;
  planTemplates?: any[];
}> = ({ isOpen, onClose, client, onSave, planTemplates }) => {
  const { register, handleSubmit, control, formState: { errors, isValid }, reset, watch, setValue, trigger } = useForm<ClientFormData>({
    mode: 'onChange',
    defaultValues: client ? {
      carnetNo: client.carnetNo || '',
      name: client.name,
      lastname: client.lastname,
      cedula: client.cedula,
      categoria: client.categoria || '',
      nacionalidad: client.nacionalidad || '',
      address: client.address,
      dateOfBirth: client.dateOfBirth,
      fechaIngreso: client.fechaIngreso || '',
      telefonos: client.telefonos || '',
      gremio: client.gremio || '',
      servicePlanId: '1' // Default plan
    } : {
      carnetNo: '',
      name: '',
      lastname: '',
      cedula: '',
      categoria: '',
      nacionalidad: '',
      address: '',
      dateOfBirth: '',
      fechaIngreso: '',
      telefonos: '',
      gremio: '',
      servicePlanId: '1' // Default plan
    }
  });

  const [currentStep, setCurrentStep] = useState(0);
  const watchedDateOfBirth = watch('dateOfBirth');

  // Resetear el formulario cuando cambie el cliente
  useEffect(() => {
    setCurrentStep(0); // Reset step on open/change
    if (client) {
      // Convertir la fecha ISO a formato YYYY-MM-DD para el input date
      const formatDateForInput = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      };

      reset({
        carnetNo: client.carnetNo || '',
        name: client.name,
        lastname: client.lastname,
        cedula: applyDominicanIDMask(client.cedula), // Aplicar formato de cédula
        categoria: client.categoria || '',
        nacionalidad: client.nacionalidad || '',
        address: client.address,
        dateOfBirth: formatDateForInput(client.dateOfBirth),
        fechaIngreso: client.fechaIngreso ? formatDateForInput(client.fechaIngreso) : '',
        telefonos: client.telefonos || '',
        gremio: client.gremio || '',
        servicePlanId: (client.plans?.[0] as any)?.planTemplateId?.toString() || '1' // Plan actual del cliente
      });
    } else {
      reset({
        carnetNo: '',
        name: '',
        lastname: '',
        cedula: '',
        categoria: '',
        nacionalidad: '',
        address: '',
        dateOfBirth: '',
        fechaIngreso: '',
        telefonos: '',
        gremio: '',
        servicePlanId: '1' // Default plan
      });
    }
  }, [client, reset]);

  const onSubmit = (data: ClientFormData) => {
    // Clean the cedula before saving
    const cleanedData = {
      ...data,
      cedula: cleanDominicanID(data.cedula)
    };
    onSave(cleanedData);
    reset();
    onClose();
    setCurrentStep(0);
  };

  const nextStep = async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent accidental submission
    const fieldsToValidate = steps[currentStep].fields as any[];
    const isStepValid = await trigger(fieldsToValidate);
    if (isStepValid) {
      setCurrentStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex flex-col">
          <div className="flex items-center space-x-3 mb-4">
            <div className="h-10 w-10 bg-primary-100 rounded-xl flex items-center justify-center border border-primary-200">
              <UsersIcon className="h-5 w-5 text-primary-700" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900">
                {client ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h3>
              <p className="text-sm text-gray-600">
                {client ? 'Modifica la información' : 'Complete los pasos para registrar'}
              </p>
            </div>
          </div>

          {/* Wizard Stepper */}
          <nav aria-label="Progress">
            <ol role="list" className="flex items-center">
              {steps.map((step, index) => (
                <li key={step.name} className={`relative flex-1 ${index !== steps.length - 1 ? 'pr-8 sm:pr-20' : ''}`}>
                  {index < steps.length - 1 && (
                    <div className="absolute top-4 left-0 h-0.5 w-full bg-gray-200" aria-hidden="true">
                      <div
                        className="h-0.5 bg-primary-600 transition-all duration-300"
                        style={{ width: index < currentStep ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                  <a href="#" className="group relative flex flex-col items-center">
                    <span className={`flex h-8 w-8 items-center justify-center rounded-full bg-white border-2 transition-colors duration-200 z-10 
                      ${index < currentStep ? 'border-primary-600 bg-primary-600' : index === currentStep ? 'border-primary-600' : 'border-gray-300'}`}>
                      {index < currentStep ? (
                        <CheckIcon className="h-5 w-5 text-white" aria-hidden="true" />
                      ) : (
                        <span className={`text-sm font-medium ${index === currentStep ? 'text-primary-600' : 'text-gray-500'}`}>
                          {index + 1}
                        </span>
                      )}
                    </span>
                    <span className={`mt-2 text-xs font-medium ${index === currentStep ? 'text-primary-600' : 'text-gray-500'}`}>
                      {step.name}
                    </span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        </div>
      }
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 flex flex-col h-full">
        {/* Step 1: Datos Personales */}
        {currentStep === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in">
            <div>
              <Input
                label="Carnet No."
                placeholder="Número de carnet"
                error={errors.carnetNo?.message}
                required
                {...register('carnetNo', {
                  required: 'Requerido',
                  minLength: { value: 1, message: 'Requerido' }
                })}
              />
            </div>
            <div>
              <Input
                label="Categoría"
                placeholder="Categoría recibos"
                error={errors.categoria?.message}
                {...register('categoria')}
              />
            </div>
            <div>
              <Input
                label="Nombre"
                placeholder="Nombres"
                error={errors.name?.message}
                required
                {...register('name', {
                  required: 'Requerido',
                  minLength: { value: 2, message: 'Min 2 caracteres' }
                })}
              />
            </div>
            <div>
              <Input
                label="Apellido"
                placeholder="Apellidos"
                error={errors.lastname?.message}
                required
                {...register('lastname', {
                  required: 'Requerido',
                  minLength: { value: 2, message: 'Min 2 caracteres' }
                })}
              />
            </div>
            <div>
              <Input
                label="Cédula"
                placeholder="000-0000000-0"
                error={errors.cedula?.message}
                required
                {...register('cedula', {
                  required: 'Requerida',
                  validate: (value) => {
                    const cleanValue = cleanDominicanID(value);
                    if (cleanValue.length !== 11) return 'Debe tener 11 dígitos';
                    // Validación más permisiva - solo verificar formato básico
                    if (!/^\d{11}$/.test(cleanValue)) {
                      return 'La cédula debe contener solo números';
                    }
                    return true;
                  }
                })}
                onChange={(value) => {
                  const maskedValue = applyDominicanIDMask(value);
                  setValue('cedula', maskedValue, { shouldValidate: true });
                }}
              />
            </div>
            <div>
              <Input
                label="Fecha de Nacimiento"
                type="date"
                error={errors.dateOfBirth?.message}
                required
                {...register('dateOfBirth', {
                  required: 'Requerida',
                  validate: (value) => {
                    if (!value) return 'Requerida';
                    if (!validateMinimumAge(value, 18)) return 'Debe ser mayor de 18';
                    return true;
                  }
                })}
                min={getMinDateOfBirth()}
                max={getMaxDateOfBirth()}
              />
              {watchedDateOfBirth && (
                <div className="mt-1 flex items-center text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                  Edad: {calculateAge(watchedDateOfBirth)} años
                </div>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Contacto */}
        {currentStep === 1 && (
          <div className="space-y-6 animate-fade-in">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Input
                  label="Nacionalidad"
                  placeholder="Dominicana, etc."
                  error={errors.nacionalidad?.message}
                  {...register('nacionalidad')}
                />
              </div>
              <div>
                <Controller
                  name="telefonos"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <PhoneInput
                      value={value}
                      onChange={onChange}
                      error={errors.telefonos?.message}
                    />
                  )}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dirección Completa *
              </label>
              <textarea
                {...register('address', { required: 'La dirección es requerida', minLength: { value: 5, message: 'Min 5 caracteres' } })}
                rows={3}
                className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${errors.address ? 'border-red-300' : 'border-gray-300'}`}
                placeholder="Calle, Número, Sector, Ciudad..."
              />
              {errors.address && <p className="mt-1 text-sm text-red-600">{errors.address.message}</p>}
            </div>
          </div>
        )}

        {/* Step 3: Afiliación */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 space-y-4">
              <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-2">Datos del Plan</h4>

              <div>
                <label className="block text-sm font-bold text-gray-800 mb-2">
                  Plan de Servicio *
                </label>
                <select
                  {...register('servicePlanId', { required: 'Plan requerido' })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white"
                >
                  <option value="">Seleccionar plan...</option>
                  {planTemplates?.map((template: any) => (
                    <option key={template.id} value={template.id}>
                      {template.name} - ${template.basePrice}
                    </option>
                  ))}
                </select>
                {errors.servicePlanId && <p className="text-sm text-red-600 mt-1">{errors.servicePlanId.message}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Fecha Inicio Plan"
                  type="date"
                  error={errors.startDate?.message}
                  {...register('startDate')}
                />
                <Input
                  label="Cuota Mensual"
                  type="number"
                  placeholder="Auto"
                  error={errors.cuotaMensual?.message}
                  {...register('cuotaMensual', { valueAsNumber: true, min: 0 })}
                />
              </div>

              <Input
                label="Inscripción"
                type="number"
                placeholder="0.00"
                error={errors.pagoInscripcion?.message}
                {...register('pagoInscripcion', { valueAsNumber: true, min: 0 })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Input
                label="Fecha de Ingreso"
                type="date"
                error={errors.fechaIngreso?.message}
                {...register('fechaIngreso')}
              />
              <Input
                label="Código Gremio"
                placeholder="Se genera automáticamente"
                disabled
                className="bg-gray-100 text-gray-500 cursor-not-allowed"
                {...register('gremio')}
              />
            </div>
          </div>
        )}

        {/* Sticky Footer */}
        <div className="sticky bottom-0 bg-white pt-4 mt-6 border-t border-gray-200 flex justify-between z-10 pb-2">
          {currentStep > 0 ? (
            <Button type="button" variant="outline" onClick={prevStep}>
              <ChevronLeftIcon className="h-4 w-4 mr-1" />
              Atrás
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
          )}

          {currentStep < steps.length - 1 ? (
            <Button type="button" variant="primary" onClick={nextStep} className="bg-primary-600 text-white">
              Siguiente
              <ChevronRightIcon className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button type="submit" variant="danger" className="bg-primary-600 hover:bg-primary-700 text-white">
              {client ? 'Guardar Cambios' : 'Crear Cliente'}
            </Button>
          )}
        </div>
      </form>
    </Modal>
  );
};


function getPlanSortKey(client: Client): string {
  const p = client.plans?.[0] as { planTemplate?: { name?: string }; name?: string } | undefined;
  const n = p?.planTemplate?.name || p?.name || '';
  return String(n).toLowerCase();
}

function sortClientsList(list: Client[], field: ClientSortField, dir: 'asc' | 'desc'): Client[] {
  const m = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (field) {
      case 'name': {
        const an = `${a.name} ${a.lastname}`.toLowerCase().trim();
        const bn = `${b.name} ${b.lastname}`.toLowerCase().trim();
        return an < bn ? -m : an > bn ? m : 0;
      }
      case 'date': {
        const at = new Date(a.createdAt).getTime();
        const bt = new Date(b.createdAt).getTime();
        return at < bt ? -m : at > bt ? m : 0;
      }
      case 'plan': {
        const ap = getPlanSortKey(a);
        const bp = getPlanSortKey(b);
        return ap < bp ? -m : ap > bp ? m : 0;
      }
      case 'family': {
        const af = a.familyMembers?.length ?? 0;
        const bf = b.familyMembers?.length ?? 0;
        return af < bf ? -m : af > bf ? m : 0;
      }
      default:
        return 0;
    }
  });
}

const Clients: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  // PROVISIONAL - Modal para activar servicio por fallecimiento
  const [isActivateServiceModalOpen, setIsActivateServiceModalOpen] = useState(false);
  const [clientForActivation, setClientForActivation] = useState<Client | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'success' | 'info';
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: () => { }
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [planFilter, setPlanFilter] = useState('');
  const [gremioFilter, setGremioFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<DerivedClientStatus | 'all'>('all');
  const [familyFilter, setFamilyFilter] = useState<FamilyCountFilter>('all');
  const [sortField, setSortField] = useState<ClientSortField>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);

  const { data: clients, isLoading } = useQuery<{ data: { data: Client[] } }>(
    'clients',
    () =>
      clientsAPI.getAll().then((response) => {
        const mappedClients = response.data.data.map((client: Record<string, unknown>) => ({
          ...client,
          plans: client.clientPlans || [],
          familyMembers: client.familyMembers || []
        }));

        return {
          ...response,
          data: {
            ...response.data,
            data: mappedClients as Client[]
          }
        };
      })
  );

  useEffect(() => {
    const editId = (location.state as { editClientId?: string } | null)?.editClientId;
    if (!editId || !clients?.data?.data?.length) return;
    const found = clients.data.data.find((c) => String(c.id) === String(editId));
    if (found) {
      setSelectedClient(found);
      setIsModalOpen(true);
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, clients?.data?.data, location.pathname, navigate]);

  const { data: planTemplates } = useQuery<{ data: { data: any[] } }>(
    'planTemplates',
    () => planTemplatesAPI.getAll()
  );

  const createMutation = useMutation(clientsAPI.create, {
    onSuccess: () => {
      queryClient.invalidateQueries('clients');
      toast.success('Cliente creado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al crear el cliente');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: ClientFormData }) => clientsAPI.update(id, data),
    {
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries('clients');
        queryClient.invalidateQueries(['client', id]);
        toast.success('Cliente actualizado exitosamente');
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Error al actualizar el cliente');
      }
    }
  );

  const deleteMutation = useMutation(clientsAPI.delete, {
    onSuccess: () => {
      queryClient.invalidateQueries('clients');
      toast.success('Cliente eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar el cliente');
    }
  });

  const deleteFamilyMutation = useMutation(familyMembersAPI.delete, {
    onSuccess: (_, id) => {
      queryClient.invalidateQueries('clients');
      queryClient.invalidateQueries(['client', id]);
      toast.success('Familiar eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error al eliminar el familiar');
    }
  });

  const handleEdit = (client: Client) => {
    setSelectedClient(client);
    setIsModalOpen(true);
  };

  const handleViewDetails = (client: Client) => {
    setSelectedClient(client);
    setIsDetailsModalOpen(true);
  };

  const handleCreate = () => {
    setSelectedClient(null);
    setIsModalOpen(true);
  };

  // PROVISIONAL - Handler para activar servicio
  const handleActivateService = (client: Client) => {
    setClientForActivation(client);
    setIsActivateServiceModalOpen(true);
  };

  const handleSave = (data: ClientFormData) => {
    // Calculate age from date of birth and map fields to backend format
    const clientData = {
      carnetNo: data.carnetNo,
      name: data.name,
      lastname: data.lastname,
      cedula: data.cedula,
      categoria: data.categoria || null,
      nacionalidad: data.nacionalidad || null,
      address: data.address,
      age: calculateAge(data.dateOfBirth),
      dateOfBirth: data.dateOfBirth,
      fechaIngreso: data.fechaIngreso || new Date().toISOString().split('T')[0],
      telefonos: data.telefonos || null,
      gremio: data.gremio || undefined, // Opcional, se genera automáticamente si no se proporciona
      planTemplateId: parseInt(data.servicePlanId), // Incluir el plan seleccionado
      cuotaMensual: data.cuotaMensual || undefined,
      pagoInscripcion: data.pagoInscripcion || null,
      startDate: data.startDate || undefined
    };

    if (selectedClient) {
      updateMutation.mutate({ id: selectedClient.id, data: clientData });
    } else {
      createMutation.mutate(clientData);
    }
  };

  const handleDelete = (id: string) => {
    setConfirmModal({
      isOpen: true,
      title: 'Eliminar Cliente',
      message: '¿Está seguro de que desea eliminar este cliente? Esta acción eliminará también todos los familiares asociados y no se puede deshacer.',
      type: 'danger',
      onConfirm: () => {
        deleteMutation.mutate(id);
        setConfirmModal(prev => ({ ...prev, isOpen: false }));
      }
    });
  };

  const allClients = useMemo(() => clients?.data?.data ?? [], [clients]);

  const startOfMonth = useMemo(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  }, []);

  const planOptions = useMemo(() => {
    const raw = planTemplates?.data?.data ?? [];
    return raw.map((p: { id: number; name: string }) => ({ id: String(p.id), name: p.name }));
  }, [planTemplates]);

  const gremioOptions = useMemo(() => {
    const s = new Set<string>();
    allClients.forEach((c) => {
      const g = (c.gremio || '').trim();
      if (g) s.add(g);
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'es'));
  }, [allClients]);

  const filteredAndSortedClients = useMemo(() => {
    let list = allClients;
    const q = searchTerm.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const full = `${c.name} ${c.lastname}`.toLowerCase();
        const addr = (c.address || '').toLowerCase();
        const cedDigits = (c.cedula || '').replace(/\D/g, '');
        const qDigits = searchTerm.replace(/\D/g, '');
        return (
          full.includes(q) ||
          addr.includes(q) ||
          (c.cedula && String(c.cedula).toLowerCase().includes(q)) ||
          (qDigits.length >= 3 && cedDigits.includes(qDigits)) ||
          (c.gremio && c.gremio.toLowerCase().includes(q))
        );
      });
    }

    if (planFilter) {
      list = list.filter((c) => {
        const p = c.plans?.[0] as { planTemplate?: { id?: number }; planTemplateId?: number } | undefined;
        const tid = p?.planTemplate?.id ?? p?.planTemplateId;
        return String(tid) === planFilter;
      });
    }

    if (gremioFilter === '__none__') {
      list = list.filter((c) => !(c.gremio || '').trim());
    } else if (gremioFilter) {
      list = list.filter((c) => (c.gremio || '').trim() === gremioFilter);
    }

    if (statusFilter !== 'all') {
      list = list.filter((c) => deriveClientStatus(c) === statusFilter);
    }

    if (familyFilter !== 'all') {
      list = list.filter((c) => {
        const n = c.familyMembers?.length ?? 0;
        if (familyFilter === 'none') return n === 0;
        if (familyFilter === 'low') return n >= 1 && n <= 3;
        if (familyFilter === 'high') return n >= 4;
        return true;
      });
    }

    return sortClientsList(list, sortField, sortDir);
  }, [
    allClients,
    searchTerm,
    planFilter,
    gremioFilter,
    statusFilter,
    familyFilter,
    sortField,
    sortDir
  ]);

  const totalWithPlan = useMemo(
    () => allClients.filter((c) => c.plans && c.plans.length > 0).length,
    [allClients]
  );

  const newThisMonthCount = useMemo(
    () => allClients.filter((c) => new Date(c.createdAt) >= startOfMonth).length,
    [allClients, startOfMonth]
  );

  const getActionHandlers = (client: Client) => ({
    onView: () => handleViewDetails(client),
    onEdit: () => handleEdit(client),
    onDelete: () => handleDelete(client.id),
    onRegisterPayment: () => {
      navigate('/payments');
      toast('En la pantalla de Pagos, seleccione el cliente para registrar un cobro.');
    },
    onViewInvoices: () => {
      navigate('/cobros?tab=facturas');
    },
    onViewFamily: () => {
      navigate(`/clients/${client.id}`, { state: { tab: 'familiares' } });
    },
    onViewHistory: () => {
      navigate(`/clients/${client.id}`, { state: { tab: 'historial' } });
    },
    onActivateService: () => handleActivateService(client)
  });

  const hasActiveFilters =
    Boolean(planFilter) ||
    Boolean(gremioFilter) ||
    statusFilter !== 'all' ||
    familyFilter !== 'all' ||
    sortField !== 'name' ||
    sortDir !== 'asc';

  const clearAllFilters = () => {
    setPlanFilter('');
    setGremioFilter('');
    setStatusFilter('all');
    setFamilyFilter('all');
    setSortField('name');
    setSortDir('asc');
  };

  const emptyTableState = (
    <div className="px-6 py-20 text-center">
      <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-slate-100 ring-1 ring-slate-200/80">
        <MagnifyingGlassIcon className="h-8 w-8 text-slate-400" aria-hidden />
      </div>
      <h3 className="text-lg font-semibold text-slate-900">Sin resultados en el directorio</h3>
      <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
        Ajuste la búsqueda o los filtros, o registre un nuevo afiliado con el botón superior.
      </p>
    </div>
  );

  return (
    <div className="space-y-4 animate-fade-in-up pb-8 md:space-y-6">
      {/* Desktop header */}
      <header className="hidden rounded-2xl border border-slate-200/90 bg-gradient-to-br from-white via-white to-slate-50/40 px-6 py-6 shadow-sm ring-1 ring-slate-100/90 md:block">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-500">Directorio</p>
            <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Clientes afiliados</h1>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              Padrón operativo: identificación, afiliación, núcleo familiar y estado de cuenta en una sola vista.
            </p>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            <ClientExportMenu
              filters={{
                search: searchTerm.trim() || undefined,
                gremio: gremioFilter || undefined
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex items-center rounded-xl px-4 py-3 text-sm font-semibold"
              size="md"
            >
              <ArrowUpTrayIcon className="mr-2 h-5 w-5 stroke-2" />
              Importar
            </Button>
            <Button
              onClick={handleCreate}
              className="inline-flex shrink-0 items-center justify-center rounded-xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-slate-900/15 transition hover:bg-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
              size="md"
            >
              <PlusIcon className="mr-2 h-5 w-5 stroke-2" />
              Nuevo cliente
            </Button>
          </div>
        </div>
      </header>

      {/* Desktop stats */}
      <div className="hidden gap-4 md:grid md:grid-cols-3">
        <Card className="border border-slate-200/90 bg-gradient-to-br from-white to-indigo-50/30 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total</p>
              <h3 className="mt-2 text-3xl font-bold tabular-nums text-slate-900">
                {isLoading ? '—' : allClients.length}
              </h3>
              <p className="mt-2 flex items-center text-xs font-medium text-slate-500">
                <ArrowTrendingUpIcon className="mr-1 h-3.5 w-3.5 text-emerald-600" aria-hidden />
                Registros en el sistema
              </p>
            </div>
            <div className="rounded-xl bg-indigo-100/90 p-3 ring-1 ring-indigo-200/60">
              <UsersIcon className="h-6 w-6 text-indigo-700" aria-hidden />
            </div>
          </div>
        </Card>

        <Card className="border border-slate-200/90 bg-gradient-to-br from-white to-emerald-50/30 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Con plan</p>
              <h3 className="mt-2 text-3xl font-bold tabular-nums text-slate-900">
                {isLoading ? '—' : totalWithPlan}
              </h3>
              <p className="mt-2 text-xs text-slate-500">Clientes con al menos un plan asociado</p>
            </div>
            <div className="rounded-xl bg-emerald-100/90 p-3 ring-1 ring-emerald-200/60">
              <UserGroupIcon className="h-6 w-6 text-emerald-700" aria-hidden />
            </div>
          </div>
        </Card>

        <Card className="border border-slate-200/90 bg-gradient-to-br from-white to-sky-50/30 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Nuevos este mes</p>
              <h3 className="mt-2 text-3xl font-bold tabular-nums text-slate-900">
                {isLoading ? '—' : newThisMonthCount}
              </h3>
              <p className="mt-2 text-xs font-medium text-sky-700">Altas desde el día 1 del mes en curso</p>
            </div>
            <div className="rounded-xl bg-sky-100/90 p-3 ring-1 ring-sky-200/60">
              <UserPlusIcon className="h-6 w-6 text-sky-700" aria-hidden />
            </div>
          </div>
        </Card>
      </div>

      {/* Mobile shell */}
      <div className="space-y-4 md:hidden">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">Clientes</h1>
            <p className="mt-1 text-sm text-slate-500">
              Gestiona los clientes afiliados y su información.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ClientExportMenu
              compact
              filters={{
                search: searchTerm.trim() || undefined,
                gremio: gremioFilter || undefined
              }}
            />
            <button
              type="button"
              onClick={() => setIsImportModalOpen(true)}
              className="inline-flex h-10 items-center justify-center rounded-xl border-2 border-primary-300 bg-white px-3 text-primary-700 shadow-sm hover:bg-primary-50"
              aria-label="Importar clientes"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
            </button>
            <Button
              onClick={handleCreate}
              size="sm"
              className="inline-flex h-10 shrink-0 items-center rounded-xl bg-primary-600 px-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-700"
            >
              <PlusIcon className="h-5 w-5" />
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre, cédula, dirección..."
              className="h-11 w-full rounded-2xl border border-[#E5EAF1] bg-white pl-10 pr-10 text-sm font-medium text-slate-900 shadow-[0_1px_2px_rgba(15,23,42,0.04)] placeholder:text-slate-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
              type="search"
              aria-label="Buscar clientes"
            />
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                aria-label="Limpiar búsqueda"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setFiltersSheetOpen(true)}
            className="relative inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-[#E5EAF1] bg-primary-50 text-primary-700 transition active:scale-95"
            aria-label="Abrir filtros"
          >
            <FunnelIcon className="h-5 w-5" />
            {hasActiveFilters ? (
              <span className="absolute right-2 top-2 h-2 w-2 rounded-full bg-primary-600 ring-2 ring-primary-50" />
            ) : null}
          </button>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-2xl border border-[#E8ECF3] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-xl bg-primary-50">
              <UsersIcon className="h-4 w-4 text-primary-600" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</p>
            <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
              {isLoading ? '—' : allClients.length}
            </p>
            <p className="text-[11px] text-slate-500">Clientes</p>
          </div>
          <div className="rounded-2xl border border-[#E8ECF3] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-50">
              <UserGroupIcon className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Con plan</p>
            <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
              {isLoading ? '—' : totalWithPlan}
            </p>
            <p className="text-[11px] text-slate-500">Activos</p>
          </div>
          <div className="rounded-2xl border border-[#E8ECF3] bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
            <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-xl bg-sky-50">
              <UserPlusIcon className="h-4 w-4 text-sky-600" />
            </div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Nuevos</p>
            <p className="mt-0.5 text-xl font-bold tabular-nums text-slate-900">
              {isLoading ? '—' : newThisMonthCount}
            </p>
            <p className="text-[11px] text-slate-500">Este mes</p>
          </div>
        </div>

        <div className="flex items-center justify-between border-b border-[#E5EAF1] pb-2">
          <p className="text-sm font-medium text-slate-600">
            <span className="tabular-nums font-semibold text-slate-900">
              {filteredAndSortedClients.length}
            </span>{' '}
            resultados
          </p>
          <button
            type="button"
            onClick={() => setFiltersSheetOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-100 active:scale-95"
          >
            <ArrowsUpDownIcon className="h-4 w-4" />
            Ordenar
          </button>
        </div>
      </div>

      <DataTableCard>
        <ClientFiltersToolbar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          planId={planFilter}
          onPlanChange={setPlanFilter}
          planOptions={planOptions}
          gremio={gremioFilter}
          onGremioChange={setGremioFilter}
          gremioOptions={gremioOptions}
          status={statusFilter}
          onStatusChange={setStatusFilter}
          family={familyFilter}
          onFamilyChange={setFamilyFilter}
          sortField={sortField}
          sortDir={sortDir}
          onSortFieldChange={setSortField}
          onSortDirToggle={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
          resultCount={filteredAndSortedClients.length}
          totalCount={allClients.length}
        />
        <ClientTable
          clients={filteredAndSortedClients}
          loading={isLoading}
          getActionHandlers={getActionHandlers}
          emptyState={emptyTableState}
          onMobileOpenDetail={(client) => navigate(`/clients/${client.id}`)}
        />
      </DataTableCard>

      <ClientFiltersSheet
        open={filtersSheetOpen}
        onClose={() => setFiltersSheetOpen(false)}
        planId={planFilter}
        onPlanChange={setPlanFilter}
        planOptions={planOptions}
        gremio={gremioFilter}
        onGremioChange={setGremioFilter}
        gremioOptions={gremioOptions}
        status={statusFilter}
        onStatusChange={setStatusFilter}
        family={familyFilter}
        onFamilyChange={setFamilyFilter}
        sortField={sortField}
        sortDir={sortDir}
        onSortFieldChange={setSortField}
        onSortDirChange={setSortDir}
        onApply={() => {}}
        onClear={clearAllFilters}
      />

      {/* Modals */}
      <ClientImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onImported={() => {
          queryClient.invalidateQueries('clients');
        }}
      />

      <ClientModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        client={selectedClient}
        onSave={handleSave}
        planTemplates={planTemplates?.data?.data}
      />

      <ClientDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        client={selectedClient}
        deleteFamilyMutation={deleteFamilyMutation}
        onEditClient={() => {
          setIsDetailsModalOpen(false);
          setIsModalOpen(true);
        }}
      />

      {/* PROVISIONAL - Modal para activar servicio */}
      <ActivateServiceModal
        isOpen={isActivateServiceModalOpen}
        onClose={() => setIsActivateServiceModalOpen(false)}
        client={clientForActivation}
        onSuccess={() => {
          queryClient.invalidateQueries('clients');
        }}
      />

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        isLoading={deleteMutation.isLoading || deleteFamilyMutation.isLoading}
      />
    </div>
  );
};

export default Clients;
